import { Fragment, useEffect, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { supabase } from '@/lib/supabase'
import { DealStatusBadge } from '../components/DealStatusBadge'
import type { DealStatus } from '../components/DealStatusBadge'

export const Route = createFileRoute('/cases/$caseNumber')({
  component: CaseDetailComponent,
})

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuctionDetail {
  case_number: string
  auction_date: string
  judgment_amount: number | null
  sold_amount: number | null
  surplus: number | null
  bidder_type: string | null
  property_address: string | null
  parcel_id: string | null
}

interface Party {
  partyID: string
  partyName: string
  partyTypeCode: string
  partyTypeDesc: string
  leadAttBarnumber: string
  leadAttName: string
  otherAttNames: string
}

interface Participant {
  id: string
  party_id: string
  name: string
}

interface CaseParticipant {
  role: 'plaintiff' | 'defendant'
  party_type_code: string | null
  lead_att_barnumber: string | null
  lead_att_name: string | null
  other_att_names: string | null
  participants: Participant
}

interface Docket {
  oDate: string
  dueDate: string
  eventDate: string
  eventTime: string
  eventType: string
  docketCode: string
  docketDescrition: string  // typo preserved from source data
  comments: string
  docketNumber: string
  bookAndPage: string
  numberOfDocuments: number
  canBuyCertCopy: boolean
  pendingRedaction: number
  completedDate: string
  partiesName: string
}

interface CourtCaseDetail {
  case_number: string
  case_year: string | null
  case_type: string | null
  case_type_description: string | null
  court_location: string | null
  filing_date: string | null
  plaintiff: string | null
  defendant: string | null
  property_address: string | null
  judgment_amount: number | null
  case_status: string | null
  last_action: string | null
  last_action_date: string | null
  judge: string | null
  metadata: { dockets?: Docket[] } | null
  case_participants: CaseParticipant[]
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

function useDealForCase(caseNumber: string) {
  return useQuery<{ id: string; status: DealStatus } | null>({
    queryKey: ['deal-for-case', caseNumber],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('id, status')
        .eq('case_number', caseNumber)
        .maybeSingle()
      if (error) throw new Error(error.message)
      return data as { id: string; status: DealStatus } | null
    },
    staleTime: 2 * 60 * 1000,
  })
}

function useCaseDetail(caseNumber: string) {
  return useQuery<{ auction: AuctionDetail | null; courtCase: CourtCaseDetail | null }>({
    queryKey: ['case', caseNumber],
    queryFn: async () => {
      const [auctionRes, courtRes] = await Promise.all([
        supabase
          .from('auctions')
          .select('case_number, auction_date, judgment_amount, sold_amount, surplus, bidder_type, property_address, parcel_id')
          .eq('case_number', caseNumber)
          .maybeSingle(),
        supabase
          .from('court_cases')
          .select(`case_number, case_year, case_type, case_type_description, court_location, filing_date, plaintiff, defendant, property_address, judgment_amount, case_status, last_action, last_action_date, judge, metadata, case_participants(role, party_type_code, lead_att_barnumber, lead_att_name, other_att_names, participants(id, party_id, name))`)
          .eq('case_number', caseNumber)
          .maybeSingle(),
      ])

      if (auctionRes.error) throw new Error(auctionRes.error.message)
      if (courtRes.error) throw new Error(courtRes.error.message)

      return {
        auction: auctionRes.data as AuctionDetail | null,
        courtCase: courtRes.data as CourtCaseDetail | null,
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number | null): string {
  if (value == null) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const [year, month, day] = value.split('-')
  if (!month) return value  // already formatted or non-ISO
  return `${month}/${day}/${year}`
}

function parseParties(raw: string | null): Party[] {
  if (!raw) return []
  try { return JSON.parse(raw) } catch { return [] }
}

function mapCaseParticipants(cps: CaseParticipant[], role: 'plaintiff' | 'defendant'): Party[] {
  return cps
    .filter(cp => cp.role === role)
    .map(cp => ({
      partyID:          cp.participants.party_id,
      partyName:        cp.participants.name,
      partyTypeCode:    cp.party_type_code ?? '',
      partyTypeDesc:    role === 'plaintiff' ? 'Plaintiff' : 'Defendant',
      leadAttBarnumber: cp.lead_att_barnumber ?? '',
      leadAttName:      cp.lead_att_name ?? '',
      otherAttNames:    cp.other_att_names ?? '',
    }))
}

function isSentinelTime(t: string): boolean {
  return !t || t.startsWith('1/1/1900') || t.startsWith('0001-01-01')
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
    </div>
  )
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  )
}

function ExpandablePartyList({
  label,
  parties,
  alwaysExpandable = false,
}: {
  label: string
  parties: Party[]
  alwaysExpandable?: boolean
}) {
  const [expandedIDs, setExpandedIDs] = useState<Set<string>>(new Set())

  function toggle(id: string) {
    setExpandedIDs(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return (
    <div className="col-span-2">
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">{label}</dt>
      {parties.length === 0 ? (
        <dd className="text-sm text-gray-400">—</dd>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
          {parties.map(party => {
            const isOpen = expandedIDs.has(party.partyID)
            const hasDetail = alwaysExpandable || party.leadAttName || party.otherAttNames
            return (
              <div key={party.partyID}>
                <button
                  type="button"
                  onClick={() => hasDetail && toggle(party.partyID)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left bg-white ${hasDetail ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                >
                  <span className="flex-1 text-sm font-medium text-gray-900">{party.partyName}</span>
                  <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                    {party.partyTypeCode}
                  </span>
                  {hasDetail && (
                    <svg
                      className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 pt-1 bg-gray-50 text-xs text-gray-600 space-y-1">
                    {party.leadAttName && (
                      <div>
                        <span className="font-medium text-gray-500">Attorney: </span>
                        {party.leadAttName}
                        {party.leadAttBarnumber && (
                          <span className="ml-1 text-gray-400">(Bar #{party.leadAttBarnumber})</span>
                        )}
                      </div>
                    )}
                    {party.otherAttNames && (
                      <div>
                        <span className="font-medium text-gray-500">Other Attorneys: </span>
                        {party.otherAttNames}
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-500">Party Type: </span>
                      {party.partyTypeDesc}
                    </div>
                    <div className="text-gray-400 select-all font-mono">{party.partyID}</div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function DocketTable({ dockets }: { dockets: Docket[] }) {
  const [expandedIdxs, setExpandedIdxs] = useState<Set<number>>(new Set())

  function toggle(i: number) {
    setExpandedIdxs(prev => {
      const next = new Set(prev)
      next.has(i) ? next.delete(i) : next.add(i)
      return next
    })
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-6 px-2" />
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-28">
              Date
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-32">
              Type
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
              Description
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Comments
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {dockets.map((d, i) => {
            const isOpen = expandedIdxs.has(i)
            const extraFields: { label: string; value: string }[] = []
            if (!isSentinelTime(d.eventTime)) extraFields.push({ label: 'Time', value: d.eventTime })
            if (d.docketCode) extraFields.push({ label: 'Docket Code', value: d.docketCode })
            if (d.docketNumber) extraFields.push({ label: 'Docket #', value: d.docketNumber })
            if (d.bookAndPage) extraFields.push({ label: 'Book & Page', value: d.bookAndPage })
            if (d.completedDate) extraFields.push({ label: 'Completed', value: d.completedDate })
            if (d.partiesName) extraFields.push({ label: 'Parties', value: d.partiesName })
            if (d.numberOfDocuments > 0) extraFields.push({ label: 'Documents', value: String(d.numberOfDocuments) })
            if (d.canBuyCertCopy) extraFields.push({ label: 'Cert Copy Available', value: 'Yes' })
            if (d.pendingRedaction > 0) extraFields.push({ label: 'Pending Redaction', value: String(d.pendingRedaction) })

            return (
              <Fragment key={i}>
                <tr
                  onClick={() => toggle(i)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-2 py-3 text-center">
                    <svg
                      className={`w-3.5 h-3.5 text-gray-400 mx-auto transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {d.eventDate || formatDate(d.oDate) || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{d.eventType || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{d.docketDescrition || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{d.comments || '—'}</td>
                </tr>
                {isOpen && (
                  <tr className="bg-gray-50">
                    <td />
                    <td colSpan={4} className="px-4 pb-3 pt-1">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
                        {extraFields.map(f => (
                          <div key={f.label}>
                            <span className="font-medium text-gray-500">{f.label}: </span>
                            <span className="text-gray-800">{f.value}</span>
                          </div>
                        ))}
                        {d.comments && (
                          <div className="col-span-2 sm:col-span-3 mt-1">
                            <span className="font-medium text-gray-500">Full Comments: </span>
                            <span className="text-gray-800">{d.comments}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function normalizeDockets(metadata: Record<string, unknown> | null | undefined): Docket[] {
  if (!metadata) return []
  if (Array.isArray(metadata.dockets)) return metadata.dockets as Docket[]
  if (Array.isArray(metadata.docketEvents)) {
    return (metadata.docketEvents as Array<{ date: string; description: string; additionalText: string | null }>).map(e => ({
      oDate: e.date,
      dueDate: '',
      eventDate: '',
      eventTime: '',
      eventType: '',
      docketCode: '',
      docketDescrition: e.description,
      comments: e.additionalText ?? '',
      docketNumber: '',
      bookAndPage: '',
      numberOfDocuments: 0,
      canBuyCertCopy: false,
      pendingRedaction: 0,
      completedDate: '',
      partiesName: '',
    }))
  }
  return []
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function CaseDetailComponent() {
  const { caseNumber } = Route.useParams()
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading: dataLoading, error: dataError } = useCaseDetail(caseNumber)
  const { data: existingDeal, isLoading: dealLoading } = useDealForCase(caseNumber)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  async function handleCreateDeal() {
    if (!user) return
    setCreating(true)
    setCreateError('')
    const { data: newDeal, error } = await supabase
      .from('deals')
      .insert({ case_number: caseNumber, created_by: user.id, status: 'new' })
      .select('id')
      .single()
    if (error) {
      if (error.code === '23505') {
        // Race condition: deal was created between our check and insert
        const { data: existing } = await supabase
          .from('deals').select('id').eq('case_number', caseNumber).single()
        if (existing) {
          navigate({ to: '/deals/$dealId', params: { dealId: existing.id } })
          return
        }
      }
      setCreating(false)
      setCreateError(error.message)
      return
    }
    await queryClient.invalidateQueries({ queryKey: ['deal-for-case', caseNumber] })
    navigate({ to: '/deals/$dealId', params: { dealId: newDeal.id } })
  }

  useEffect(() => {
    if (!isLoading && !user) {
      navigate({ to: '/login' })
    }
  }, [user, isLoading, navigate])

  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  const { auction, courtCase } = data ?? {}
  const dockets: Docket[] = normalizeDockets(courtCase?.metadata ?? null)
  const caseParticipants = courtCase?.case_participants ?? []
  const plaintiffs = caseParticipants.length > 0
    ? mapCaseParticipants(caseParticipants, 'plaintiff')
    : parseParties(courtCase?.plaintiff ?? null)
  const defendants = caseParticipants.length > 0
    ? mapCaseParticipants(caseParticipants, 'defendant')
    : parseParties(courtCase?.defendant ?? null)

  return (
    <div>
      <div className="mb-6 flex items-center gap-4 flex-wrap">
        <button
          onClick={() => navigate({ to: '/' })}
          className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          ← Back
        </button>
        <h2 className="text-lg font-medium text-gray-900">Case {caseNumber}</h2>
        <div className="ml-auto flex items-center gap-2">
          {createError && <span className="text-sm text-red-600">{createError}</span>}
          {!dealLoading && (
            existingDeal ? (
              <button
                onClick={() => navigate({ to: '/deals/$dealId', params: { dealId: existingDeal.id } })}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
              >
                View Deal <DealStatusBadge status={existingDeal.status} />
              </button>
            ) : (
              <button
                onClick={handleCreateDeal}
                disabled={creating || dataLoading}
                className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Deal'}
              </button>
            )
          )}
        </div>
      </div>

      {dataLoading && (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500">Loading case data...</p>
        </div>
      )}

      {dataError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">Error loading case: {(dataError as Error).message}</p>
        </div>
      )}

      {!dataLoading && !dataError && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Auction Info */}
            <SectionCard title="Auction Details">
              {auction ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <Field label="Case Number" value={auction.case_number} />
                  <Field label="Auction Date" value={formatDate(auction.auction_date)} />
                  <Field label="Surplus" value={formatCurrency(auction.surplus)} />
                  <Field label="Sold Amount" value={formatCurrency(auction.sold_amount)} />
                  <Field label="Judgment Amount" value={formatCurrency(auction.judgment_amount)} />
                  <Field label="Bidder Type" value={auction.bidder_type} />
                  <Field label="Property Address" value={auction.property_address} />
                  <Field label="Parcel ID" value={auction.parcel_id} />
                </dl>
              ) : (
                <p className="text-sm text-gray-500">No auction record found.</p>
              )}
            </SectionCard>

            {/* Court Case Info */}
            <SectionCard title="Court Case">
              {courtCase ? (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <Field label="Status" value={courtCase.case_status} />
                  <Field label="Filing Date" value={formatDate(courtCase.filing_date)} />
                  <Field label="Judge" value={courtCase.judge} />
                  <Field label="Court Location" value={courtCase.court_location} />
                  <Field label="Case Type" value={courtCase.case_type_description} />
                  <Field label="Judgment Amount" value={formatCurrency(courtCase.judgment_amount)} />
                  <Field label="Last Action" value={courtCase.last_action} />
                  <Field label="Last Action Date" value={formatDate(courtCase.last_action_date)} />
                  <div className="col-span-2">
                    <Field label="Property Address" value={courtCase.property_address} />
                  </div>
                  <ExpandablePartyList label="Plaintiff" parties={plaintiffs} />
                  <ExpandablePartyList label="Defendant" parties={defendants} alwaysExpandable />
                </dl>
              ) : (
                <p className="text-sm text-gray-500">No court case record found.</p>
              )}
            </SectionCard>
          </div>

          {/* Dockets */}
          <SectionCard title={`Dockets${dockets.length ? ` (${dockets.length})` : ''}`}>
            {dockets.length === 0 ? (
              <p className="text-sm text-gray-500">No docket entries available.</p>
            ) : (
              <DocketTable dockets={dockets} />
            )}
          </SectionCard>
        </div>
      )}
    </div>
  )
}
